import { z } from 'zod';
import { AxonEventSchema } from '@reflex/shared';
import {
  StoredFlowApprovalResponseSchema,
  FlowDefinitionSchema,
  FlowEdgeSchema,
  FlowInputFieldSchema,
  FlowNodeSchema,
  FlowNodeTypeSchema,
  FlowRunNodeStatusSchema,
  FlowRunStatusSchema,
  FlowTriggerSchema,
} from '@reflex/flows';

/**
 * Snapshot of a single node from a completed flow run. Captures the
 * status the node ended in, its timing, its final IO, and (for
 * agent/agent_task nodes) a reference to the saved agent replay that
 * was created from its devbox stream at save time.
 *
 * `agentReplayId` points at a `SavedReplay` row owned by the
 * `plugin-replay` plugin. The flow replay viewer drills into a node by
 * embedding `<EmbeddableReplay>` against that id — the agent stream
 * is never re-rolled by this plugin. Resolves through the authed
 * `/api/replays/:id` endpoint inside the editor and through the
 * canonical share-link endpoint `/api/replays/public/:id` on public
 * pages.
 */
export const FlowReplayNodeSnapshotSchema = z.object({
  nodeId: z.string(),
  nodeType: FlowNodeTypeSchema,
  nodeName: z.string(),
  status: FlowRunNodeStatusSchema,
  startedAt: z.number().nullable(),
  completedAt: z.number().nullable(),
  iteration: z.number().int().min(1),
  outputLabel: z.string().nullable().optional(),
  output: z.record(z.string(), z.unknown()).nullable().optional(),
  input: z.record(z.string(), z.unknown()).nullable().optional(),
  approvalResponse: StoredFlowApprovalResponseSchema.nullable().optional(),
  agentReplayId: z.string().nullable().optional(),
});
export type FlowReplayNodeSnapshot = z.infer<typeof FlowReplayNodeSnapshotSchema>;

/** The captured flow graph: identity plus the node/edge definition. */
const FlowReplayFlowSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  nodes: z.array(FlowNodeSchema).min(1),
  edges: z.array(FlowEdgeSchema),
});

/**
 * Immutable snapshot of a flow run, including the flow definition at
 * the time of capture, the run-level metadata, per-node outcomes, and
 * the time-sorted `flow.*` Axon events emitted while the run executed.
 *
 * Per-agent-node `turn.*` / `agent.*` events are NOT inlined here. They
 * live in their own `SavedReplay` rows owned by `plugin-replay` and are
 * reached via `nodes[i].agentReplayId`. This keeps the payload small
 * and reuses the per-agent share-link gating that already exists in
 * `plugin-replay`.
 */
export const FlowReplayDataSchema = z.object({
  version: z.literal(1),
  flow: FlowReplayFlowSchema,
  run: z.object({
    id: z.string(),
    status: FlowRunStatusSchema,
    startedAt: z.number(),
    completedAt: z.number().nullable(),
    triggerData: z.record(z.string(), z.unknown()).nullable(),
  }),
  nodes: z.array(FlowReplayNodeSnapshotSchema),
  events: z.array(AxonEventSchema),
});
export type FlowReplayData = z.infer<typeof FlowReplayDataSchema>;

/**
 * Read-side shape for rows in the flow-replay table. Rows written before
 * triggers became graph nodes carry a flow-level `trigger` and `inputSchema`;
 * `parseData` folds both into the node list via `upgradeGraphData` before
 * handing back a canonical {@link FlowReplayData}.
 *
 * Only the read path accepts these fields. The write surface
 * ({@link CreateFlowReplaySchema} / {@link UpdateFlowReplaySchema}) uses the
 * canonical schema, so callers cannot set them on a new or updated snapshot.
 */
export const StoredFlowReplayDataSchema = FlowReplayDataSchema.extend({
  flow: FlowReplayFlowSchema.extend({
    trigger: FlowTriggerSchema.optional(),
    inputSchema: z.array(FlowInputFieldSchema).optional(),
  }),
});
export type StoredFlowReplayData = z.infer<typeof StoredFlowReplayDataSchema>;

export const SavedFlowReplaySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  /**
   * When the replay was made publicly readable, or `null` while private.
   * Only rows with this set are served by `/flow-replays/public/:id` —
   * saving a replay does not publish it.
   */
  sharedAt: z.number().nullable(),
  data: FlowReplayDataSchema,
});
export type SavedFlowReplay = z.infer<typeof SavedFlowReplaySchema>;

export const CreateFlowReplaySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  data: FlowReplayDataSchema,
});
export type CreateFlowReplay = z.infer<typeof CreateFlowReplaySchema>;

/**
 * Content edits only. Publishing is deliberately not part of this schema:
 * the share link is toggled through the dedicated `/flow-replays/:id/share`
 * routes, which carry a stricter gate than `flows:write`, so neither the
 * PATCH route nor the MCP `update` operation can make a replay public.
 */
export const UpdateFlowReplaySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  data: FlowReplayDataSchema.optional(),
});
export type UpdateFlowReplay = z.infer<typeof UpdateFlowReplaySchema>;

export const CreateFlowReplayFromRunSchema = z.object({
  flowRunId: z.string().min(1, 'flowRunId is required'),
  name: z.string().optional(),
  description: z.string().optional(),
});
export type CreateFlowReplayFromRun = z.infer<typeof CreateFlowReplayFromRunSchema>;

/**
 * Re-export {@link FlowDefinitionSchema} so consumers that build a
 * `FlowReplayData.flow` payload from an existing FlowDefinition can
 * stay decoupled from the engine package.
 */
export { FlowDefinitionSchema };
